import palette from "assets/theme/base/palette";
import React, { useEffect, useState } from "react";
import Typography from "@mui/material/Typography";
import { useMediaQuery } from "@mui/material";
import MDButton from "components/MDButton";
import {
  Checkbox,
  FormControlLabel,
  FormLabel,
  TextField,
} from "@mui/material";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { call } from "services/axiosService/core";
import { useMaterialUIController, setProfile, setPersona } from "context";
import CircularProgress from "@mui/material/CircularProgress";
import { deleteUser } from "services/awsService/awsService";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import { useNavigate } from "react-router-dom";
import InputAdornment from "@mui/material/InputAdornment";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Icon from "@mui/material/Icon";
import { Country, State, City } from "country-state-city";
import Autocomplete from "@mui/material/Autocomplete";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import PropTypes from "prop-types";
import * as Sentry from "@sentry/browser";
import { useTranslation } from "react-i18next";

/* ── Shared sub-components ───────────────────────────────────────── */

const StepDots = ({ current, total }) => (
  <MDBox display="flex" justifyContent="center" gap={1} mb={3}>
    {Array.from({ length: total }).map((_, i) => (
      <MDBox
        key={i}
        sx={{
          width: i === current ? 24 : 8,
          height: 8,
          borderRadius: 4,
          bgcolor: i === current ? palette.brand[500] : palette.brand[100],
          transition: "all 300ms ease",
        }}
      />
    ))}
  </MDBox>
);

const ScreenHeader = ({ icon, title, subtitle }) => (
  <MDBox textAlign="center" mb={3}>
    <MDBox
      sx={{
        width: 72,
        height: 72,
        borderRadius: "50%",
        bgcolor: palette.brand[50],
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mx: "auto",
        mb: 2,
      }}
    >
      <Icon sx={{ fontSize: "36px !important", color: palette.brand[500] }}>{icon}</Icon>
    </MDBox>
    <MDTypography variant="h5" fontWeight="bold" mb={0.5}>
      {title}
    </MDTypography>
    {subtitle && (
      <MDTypography variant="body2" color="secondary" lineHeight={1.6}>
        {subtitle}
      </MDTypography>
    )}
  </MDBox>
);

const FieldLabel = ({ children, required }) => (
  <MDBox display="flex" alignItems="center" mb={0.5} mt={1.5}>
    {required && <span style={{ color: "red", fontSize: 11, marginRight: 4 }}>*</span>}
    <MDTypography variant="caption" fontWeight="bold" sx={{ fontSize: "0.82rem" }}>
      {children}
    </MDTypography>
  </MDBox>
);

const InfoBanner = ({ icon = "info", color = palette.brand[500], bg = palette.brand[50], children }) => (
  <MDBox
    sx={{
      bgcolor: bg,
      borderRadius: 2,
      p: 2,
      mb: 2,
      display: "flex",
      alignItems: "flex-start",
      gap: 1.5,
    }}
  >
    <Icon sx={{ color, fontSize: 20, mt: "2px" }}>{icon}</Icon>
    <MDTypography variant="body2" color="text" lineHeight={1.65}>
      {children}
    </MDTypography>
  </MDBox>
);

/* ── Main Component ──────────────────────────────────────────────── */

const AdultOnBoardingScreen = ({ role, setOpenLoader }) => {
  const { t } = useTranslation();
  const { user, signOut } = useAuthenticator((context) => [context.user]);
  const [open, setOpen] = useState(false);
  const [showError, setShowError] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [personaName, setPersonaName] = useState([]);
  const [statesList, setStatesList] = useState([]);
  const [citiesList, setCitiesList] = useState([]);
  const [stateCodes, setStateCodes] = useState({});
  const [countryList, setCountryList] = useState([]);
  const [countryCodes, setCountryCodes] = useState({});
  const [countryCode, setCountryCode] = useState("");
  const [birthdate, setBirthdate] = useState("");
  const [selectedSignupType, setSelectedSignupType] = useState("parent");
  const [isAthlete, setIsAthlete] = useState(false);
  const [isCoach, setIsCoach] = useState(false);
  const [screen, setScreen] = useState(0);
  const [age, setAge] = useState(0);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [controller, dispatch] = useMaterialUIController();
  const [parentName, setParentName] = useState();
  const [notEligibleUser, setNotEligibleUser] = useState(false);
  const [inProgress, setInProgress] = useState(false);
  const [privacyPolicyChecked, setPrivacyPolicyChecked] = useState(false);
  const [termsConditionsChecked, setTermsConditionsChecked] = useState(false);
  const [messagesPolicyChecked, setMessagesPolicyChecked] = useState(false);

  const genderOptions = [
    { label: t("formLabels.male"), value: "Male" },
    { label: t("formLabels.female"), value: "Female" },
  ];
  const [gender, setGender] = useState(genderOptions[0].value);

  const { profile } = controller;
  const isSharedAccount = profile.type && profile.type.includes("Shared");
  const navigate = useNavigate();

  /* ── Handlers (unchanged) ──────────────────────────────────────── */

  useEffect(() => {
    if (profile.status.toLowerCase() === "inactive") {
      if (user && user.attributes) {
        const name_value = user.attributes.name;
        const user_dob = profile.details.dob;
        const user_gender = profile.details.gender;
        if (name_value) {
          setFirstName(name_value.split(" ")[0] ? name_value.split(" ")[0] : "");
          setLastName(name_value.split(" ")[1] ? name_value.split(" ")[1] : "");
        }
        if (user_dob) setBirthdate(user_dob);
        if (profile.relationships.coaches && !isSharedAccount) {
          setGender(user_gender);
          setIsAthlete(true);
          setPersonaName([t("formLabels.athlete")]);
        }
        if (profile.relationships?.children?.length > 0) {
          updateSignupAs("parent");
          setPersonaName([t("formLabels.parent")]);
        }
      }
      handleOpen();
    }
  }, []);

  function loadCountries() {
    const codes = {};
    Country.getAllCountries().forEach((c) => { codes[c.name] = c.isoCode; });
    setCountryCodes(codes);
    setCountryList(Object.keys(codes));
  }

  function loadStates(country) {
    setState(""); setCity("");
    const codes = {};
    const cc = countryCodes[country];
    setCountryCode(cc);
    State.getStatesOfCountry(cc).forEach((s) => { codes[s.name] = s.isoCode; });
    setStateCodes(codes);
    setStatesList(Object.keys(codes));
  }

  function loadCities(state) {
    setCity("");
    const stateCode = stateCodes[state];
    setCitiesList(City.getCitiesOfState(countryCode, stateCode).map((c) => c.name));
  }

  useEffect(() => { loadCountries(); }, []);

  useEffect(() => {
    if (personaName.length === 0) return;
    if (personaName.includes(t("formLabels.athlete"))) updateSignupAs("athlete");
    else if (personaName.includes(t("formLabels.coach"))) updateSignupAs("coach");
    else updateSignupAs("parent");
  }, [personaName]);

  const birthdateHandler = (e) => {
    setNotEligibleUser(false);
    const birthday = e.target.value;
    setBirthdate(birthday);
    const diff_ms = Date.now() - new Date(birthday).getTime();
    const user_age = Math.abs(new Date(diff_ms).getUTCFullYear() - 1970);
    setAge(user_age);
    setNotEligibleUser(user_age < 18);
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const logOutHandler = () => {
    localStorage.removeItem("session");
    localStorage.removeItem("referral");
    signOut();
  };

  const completeHandler = () => {
    let type = [];
    let payment_status = profile.payment_status;
    if (isSharedAccount) {
      window.pendo.track("Shared Account Onboarding Completed");
      type = profile.type;
    } else if (isCoach) {
      window.pendo.track("Coach/League Admin Onboarding Completed");
      type = profile.type;
    } else if (isAthlete) {
      window.pendo.track("Athlete/Adult Onboarding Completed");
      type = ["Learner", "Adult"];
      payment_status = profile.payment_status == "success" ? "success" : "pending";
    } else {
      window.pendo.track("Adult Onboarding Completed");
      type = profile.type;
    }
    const name = firstName + " " + lastName;
    const data = {
      pk: profile.pk ?? "user",
      sk: profile.sk ?? `user[${profile.id}]`,
      id: profile.id ?? null,
      cognito_user_id: profile.cognito_user_id ?? user.getUsername(),
      email: profile.email ?? null,
      true_mindsets: profile.true_mindsets,
      subscription_type: profile.subscription_type ?? null,
      auto_renew: true,
      true_mindsets_id: null,
      status: "active",
      signup_at: new Date(),
      phone: phone != "" ? phone : null,
      relationships: profile.relationships,
      payment_status,
      type,
      details: {
        name: profile.details.name ?? name,
        dob: profile.details.dob ?? birthdate,
        persona: profile.details.persona ?? personaName,
        gender: profile.details.gender ?? gender,
        address: {
          street: profile.details.address.street ?? street,
          city: profile.details.address.city ?? city,
          state: profile.details.address.state ?? state,
          country: profile.details.address.country ?? country,
          zip: profile.details.address.zip ?? zipCode,
        },
        ...(isSharedAccount && profile.details.learner_details
          ? { learner_details: profile.details.learner_details }
          : {}),
      },
      identities: [{
        google: { id: profile.identities[0].google.id ?? null },
        facebook: { id: profile.identities[0].facebook.id ?? null },
        apple: { id: profile.identities[0].apple.id ?? null },
        amazon: { id: profile.identities[0].amazon.id ?? null },
        email: { id: profile.cognito_user_id ?? user.getUsername() },
      }],
    };

    call(`users/${profile.id}`, "PUT", { body: data })
      .then((res) => {
        if (res.status == 200) {
          let details = res.data.ModifiedAttributes;
          details.id = res.data.Id;
          const usertype = details.type;
          if (usertype.includes("Shared")) setPersona(dispatch, "adultAthlete");
          else if (usertype.length == 1) {
            usertype[0].toLowerCase() === "learner" && setPersona(dispatch, "athlete");
            usertype[0].toLowerCase() === "adult" && setPersona(dispatch, "guardian");
          } else if (type.length == 2) {
            usertype[0].toLowerCase() === "learner" && setPersona(dispatch, "adultAthlete");
            usertype[0].toLowerCase() === "adult" && setPersona(dispatch, "guardian");
          } else {
            setPersona(dispatch, "guardian");
          }
          setProfile(dispatch, details);
          if (isSharedAccount) setScreen(5);
          else if (isCoach) setScreen(6);
          else if (isAthlete) setScreen(details.payment_status == "success" ? 4 : 3);
          else setScreen(6);
        } else if (res.status == 440) {
          handleClose();
        } else if (res.status == 400) {
          setShowError(true);
        }
      })
      .catch((err) => { setShowError(true); Sentry.captureException(err); });
  };

  const finishHandler = () => { handleClose(); if (!isCoach) setOpenLoader(true); };

  const onBoardingNextHandler = (e) => {
    e.preventDefault();
    if (notEligibleUser) { setOpen(false); setOpenDeleteModal(true); }
    else if (isSharedAccount || profile.relationships?.children?.length > 0) completeHandler();
    else setScreen(2);
  };

  const updateSignupAs = (value) => {
    setSelectedSignupType(value);
    setIsAthlete(value == "athlete");
    setIsCoach(value == "coach");
  };

  const handleCloseDeleteModal = () => {
    setInProgress(true);
    call(`users/${profile.id}`, "Delete", {})
      .then((res) => {
        if (res.status == 200) { setOpenDeleteModal(false); localStorage.removeItem("session"); signOut(); }
        else if (res.status == 440) setOpenDeleteModal(false);
      })
      .catch((err) => { setInProgress(false); Sentry.captureException(err); });
  };

  /* ── Render ──────────────────────────────────────────────────────── */

  const SignupOptionCard = ({ value, label, description }) => (
    <MDBox
      sx={{
        border: `2px solid ${selectedSignupType === value ? palette.brand[500] : "#e0e0e0"}`,
        borderRadius: 2,
        p: 2,
        mb: 1.5,
        cursor: "pointer",
        bgcolor: selectedSignupType === value ? palette.brand[50] : "#fff",
        transition: "all 200ms ease",
        "&:hover": { borderColor: palette.brand[400], bgcolor: palette.brand[50] },
      }}
      onClick={() => updateSignupAs(value)}
    >
      <FormControlLabel
        value={value}
        control={<Radio sx={{ color: palette.brand[400], "&.Mui-checked": { color: palette.brand[500] } }} />}
        label={
          <MDBox>
            <MDTypography variant="body2" fontWeight="bold">{label}</MDTypography>
            <MDTypography variant="caption" color="secondary" lineHeight={1.5} display="block">
              {description}
            </MDTypography>
          </MDBox>
        }
        sx={{ m: 0, width: "100%", alignItems: "flex-start" }}
      />
    </MDBox>
  );

  return (
    <>
      {/* ── Main Onboarding Dialog ─────────────────────────────────── */}
      <Dialog
        open={open}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        {/* Gradient header */}
        <MDBox
          sx={{
            background: `linear-gradient(135deg, ${palette.brand[400]} 0%, ${palette.brand[700]} 100%)`,
            py: 2.5,
            px: 3,
            textAlign: "center",
          }}
        >
          <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#fff" }}>
            {t("messages.onboarding.welcome1").split("!")[0]}
          </MDTypography>
        </MDBox>

        <DialogContent sx={{ px: { xs: 3, sm: 5 }, py: 4 }}>

          {/* Screen 0 — Welcome */}
          {screen === 0 && (
            <MDBox>
              <ScreenHeader
                icon="waving_hand"
                title={t("messages.onboarding.welcome1")}
                subtitle={t("messages.onboarding.welcome2")}
              />
              <StepDots current={0} total={3} />
              <MDButton
                variant="contained"
                color="info"
                fullWidth
                onClick={() => setScreen(1)}
                sx={{ borderRadius: 2, py: 1.5 }}
                endIcon={<Icon>arrow_forward</Icon>}
              >
                {t("buttonTitles.next")}
              </MDButton>
            </MDBox>
          )}

          {/* Screen 1 — Personal Info */}
          {screen === 1 && (
            <MDBox component="form" onSubmit={onBoardingNextHandler}>
              <ScreenHeader icon="person" title={t("headings.personalInfo")} />
              <StepDots current={1} total={3} />

              {isSharedAccount && (
                <InfoBanner icon="info" color={palette.brand[500]} bg={palette.brand[50]}>
                  <strong>{t("messages.onboarding.sharedAccountLabel")}</strong>
                  <br />
                  {t("messages.onboarding.sharedAccountParentNote")}
                </InfoBanner>
              )}

              <MDBox display="flex" gap={2}>
                <MDBox flex={1}>
                  <FieldLabel required>{t("formLabels.firstName")}</FieldLabel>
                  <TextField
                    name="firstname" type="text" size="small" value={firstName} fullWidth required
                    onChange={(e) => { if (e.nativeEvent.data != " ") setFirstName(e.target.value); }}
                  />
                </MDBox>
                <MDBox flex={1}>
                  <FieldLabel required>{t("formLabels.lastName")}</FieldLabel>
                  <TextField
                    name="lastname" type="text" size="small" value={lastName} fullWidth required
                    onChange={(e) => { if (e.nativeEvent.data != " ") setLastName(e.target.value); }}
                  />
                </MDBox>
              </MDBox>

              <FieldLabel required>{t("formLabels.dateOfBirth")}</FieldLabel>
              <TextField
                name="birthdate" type="date" size="small" value={birthdate} required fullWidth
                inputProps={{ max: new Date().toISOString().slice(0, 10) }}
                onChange={birthdateHandler}
              />
              {notEligibleUser && (
                <MDTypography variant="caption" color="error" display="block" mt={0.5}>
                  {t("messages.onboarding.notEligible")}
                </MDTypography>
              )}

              <FieldLabel required>{t("formLabels.iAm")}</FieldLabel>
              <Autocomplete
                size="small" multiple
                options={[t("formLabels.athlete"), t("formLabels.parent"), t("formLabels.coach"), t("formLabels.other")]}
                value={personaName}
                onChange={(_, newValue) => setPersonaName([...newValue])}
                limitTags={2}
                renderInput={(params) => (
                  <TextField
                    name="personaName" required={personaName.length == 0} size="small"
                    {...params} inputProps={{ ...params.inputProps }} variant="outlined"
                  />
                )}
              />

              <FieldLabel>{t("formLabels.phoneNumber")}</FieldLabel>
              <TextField
                name="phone" type="text" size="small" inputProps={{ maxLength: 10 }}
                value={phone} error={phone.length < 10 && phone.length > 0} fullWidth
                helperText={phone.length < 10 && phone.length > 0 ? "Enter a valid 10-digit number" : ""}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MDTypography variant="body2" fontWeight="medium">+1</MDTypography>
                    </InputAdornment>
                  ),
                }}
                onChange={(e) => {
                  const re = /^[0-9\b]+$/;
                  if (e.target.value === "" || re.test(e.target.value)) setPhone(e.target.value);
                }}
              />

              <FieldLabel required>{t("formLabels.sexAtBirth")}</FieldLabel>
              <Autocomplete
                size="small" multiple={false} options={genderOptions}
                getOptionLabel={(o) => o.label}
                value={genderOptions.find((o) => o.value === gender) || null}
                onChange={(_, newValue) => setGender(newValue ? newValue.value : null)}
                defaultValue={genderOptions[0]} limitTags={1}
                renderInput={(params) => (
                  <TextField size="small" required {...params}
                    inputProps={{ ...params.inputProps, autoComplete: "new-gender" }}
                    variant="outlined"
                  />
                )}
              />

              {/* Address */}
              <MDBox mt={2} mb={1}>
                <MDTypography variant="subtitle2" fontWeight="bold">
                  {t("headings.homeAddress")}
                </MDTypography>
              </MDBox>

              <FieldLabel>{t("formLabels.street")}</FieldLabel>
              <TextField name="street" type="text" size="small" value={street} fullWidth
                onChange={(e) => setStreet(e.target.value)} />

              <FieldLabel>{t("formLabels.country")}</FieldLabel>
              <Autocomplete
                size="small" options={countryList} value={country}
                onChange={(_, newValue) => { setCountry(newValue); loadStates(newValue); }}
                renderInput={(params) => (
                  <TextField size="small" {...params}
                    inputProps={{ ...params.inputProps, autoComplete: "new-country" }}
                    variant="outlined"
                  />
                )}
              />

              <MDBox display="flex" gap={2}>
                <MDBox flex={1}>
                  <FieldLabel>{t("formLabels.state")}</FieldLabel>
                  <Autocomplete
                    size="small" options={statesList} value={state}
                    onChange={(_, newValue) => { setState(newValue); loadCities(newValue); }}
                    renderInput={(params) => (
                      <TextField size="small" {...params}
                        inputProps={{ ...params.inputProps, autoComplete: "new-state" }}
                        variant="outlined"
                      />
                    )}
                  />
                </MDBox>
                <MDBox flex={1}>
                  <FieldLabel>{t("formLabels.city")}</FieldLabel>
                  <Autocomplete
                    size="small" options={citiesList} value={city}
                    onChange={(_, newValue) => setCity(newValue)}
                    renderInput={(params) => (
                      <TextField size="small" {...params}
                        inputProps={{ ...params.inputProps, autoComplete: "new-city" }}
                        variant="outlined"
                      />
                    )}
                  />
                </MDBox>
              </MDBox>

              <FieldLabel required>{t("formLabels.zip")}</FieldLabel>
              <TextField
                name="zip" type="text" size="small" value={zipCode} fullWidth required
                inputProps={{ maxLength: 5, minLength: 5 }}
                onChange={(e) => {
                  const re = /^[0-9\b]+$/;
                  if (e.target.value === "" || re.test(e.target.value)) setZipCode(e.target.value);
                }}
              />

              {/* Checkboxes */}
              <MDBox
                mt={2}
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <FormControlLabel
                  control={<Checkbox required size="small" checked={messagesPolicyChecked} onChange={() => setMessagesPolicyChecked(!messagesPolicyChecked)} />}
                  label={<MDTypography variant="caption"><span style={{ color: "#f44336" }}>* </span>{t("formLabels.emailTerms")}</MDTypography>}
                />
                <FormControlLabel
                  control={<Checkbox required size="small" checked={privacyPolicyChecked} onChange={() => setPrivacyPolicyChecked(!privacyPolicyChecked)} />}
                  label={<MDTypography variant="caption"><span style={{ color: "#f44336" }}>* </span>{t("formLabels.termsAndConditions")}{" "}<Typography component="a" fontSize={12} sx={{ color: palette.brand[500] }} href="/privacy_policy" target="_blank">{t("formLabels.privacy")}</Typography></MDTypography>}
                />
                <FormControlLabel
                  control={<Checkbox required size="small" checked={termsConditionsChecked} onChange={() => setTermsConditionsChecked(!termsConditionsChecked)} />}
                  label={<MDTypography variant="caption"><span style={{ color: "#f44336" }}>* </span>{t("formLabels.termsAndConditions")}{" "}<Typography component="a" fontSize={12} sx={{ color: palette.brand[500] }} href="/terms_conditions" target="_blank">{t("formLabels.terms")}</Typography></MDTypography>}
                />
              </MDBox>

              {showError && (
                <MDTypography variant="caption" color="error" display="block" textAlign="center" mt={1}>
                  {t("messages.onboarding.error")}
                </MDTypography>
              )}

              <MDBox display="flex" gap={2} mt={3}>
                <MDButton variant="outlined" color="secondary" fullWidth
                  onClick={() => setScreen(0)} sx={{ borderRadius: 2 }}
                  startIcon={<Icon>arrow_back</Icon>}>
                  {t("buttonTitles.previous")}
                </MDButton>
                <MDButton variant="contained" color="info" fullWidth type="submit"
                  disabled={phone.length < 10 && phone.length > 0}
                  sx={{ borderRadius: 2 }} endIcon={<Icon>arrow_forward</Icon>}>
                  {t("buttonTitles.next")}
                </MDButton>
              </MDBox>
            </MDBox>
          )}

          {/* Screen 2 — What brings you here */}
          {screen === 2 && (
            <MDBox>
              <ScreenHeader icon="help_outline" title={t("messages.onboarding.whatBringsYouHere")} />
              <StepDots current={2} total={3} />

              <RadioGroup value={selectedSignupType}
                onChange={(e) => setSelectedSignupType(e.target.value)}>
                <SignupOptionCard value="parent"
                  label={t("buttonTitles.signupToAddChild")}
                  description={t("messages.onboarding.parentDescription")} />
                <SignupOptionCard value="athlete"
                  label={t("buttonTitles.signupAsAthlete")}
                  description={t("messages.onboarding.athleteDescription")} />
                <SignupOptionCard value="coach"
                  label={t("buttonTitles.signupAsCoach")}
                  description={t("messages.onboarding.coachDescription")} />
              </RadioGroup>

              <MDBox display="flex" gap={2} mt={2}>
                <MDButton variant="outlined" color="secondary" fullWidth
                  onClick={() => setScreen(1)} sx={{ borderRadius: 2 }}
                  startIcon={<Icon>arrow_back</Icon>}>
                  {t("buttonTitles.previous")}
                </MDButton>
                <MDButton variant="contained" color="info" fullWidth
                  disabled={!selectedSignupType} onClick={completeHandler}
                  sx={{ borderRadius: 2 }} endIcon={<Icon>rocket_launch</Icon>}>
                  {t("buttonTitles.finish")}
                </MDButton>
              </MDBox>
            </MDBox>
          )}

          {/* Screen 3 — Registration + Payment pending */}
          {screen === 3 && (
            <MDBox>
              <ScreenHeader icon="payment" title={t("messages.onboarding.registrationThankYou")} />
              <InfoBanner icon="info" color={palette.brand[500]} bg={palette.brand[50]}>
                {t("messages.onboarding.completePayment")}{" "}
                <strong
                  style={{ color: palette.brand[500], cursor: "pointer" }}
                  onClick={() => navigate("/profile", { state: { openPayment: true } })}
                >
                  {t("buttonTitles.profile")}
                </strong>
              </InfoBanner>
              <InfoBanner icon="check_circle" color="#43a047" bg="#e8f5e9">
                {t("messages.onboarding.completedPayment")}
              </InfoBanner>
              <InfoBanner icon="mail_outline" color="#e65100" bg="#fff3e0">
                {t("messages.onboarding.contactSupport")}{" "}
                <a href="mailto:support@maxu.co" style={{ color: palette.brand[500], fontWeight: 600 }}>
                  support@maxu.co
                </a>
              </InfoBanner>
              <MDButton variant="contained" color="info" fullWidth
                onClick={() => navigate("/profile", { state: { openPayment: true } })}
                sx={{ borderRadius: 2 }} startIcon={<Icon>account_circle</Icon>}>
                {t("buttonTitles.profile")}
              </MDButton>
            </MDBox>
          )}

          {/* Screen 4 — Athlete payment success */}
          {screen === 4 && (
            <MDBox>
              <ScreenHeader icon="check_circle" title={t("messages.onboarding.completedAdult")} />
              <MDButton variant="contained" color="info" fullWidth
                onClick={() => { setOpen(false); setOpenLoader(true); }}
                sx={{ borderRadius: 2 }}>
                {t("buttonTitles.close")}
              </MDButton>
            </MDBox>
          )}

          {/* Screen 5 — Shared account setup complete */}
          {screen === 5 && (
            <MDBox>
              <ScreenHeader icon="group" title={t("messages.onboarding.sharedAccountTitle")} />

              <MDTypography variant="body2" color="text" lineHeight={1.75} mb={2}>
                {t("messages.onboarding.sharedAccountDescription", {
                  learnerName: profile.details.learner_details?.name || t("formLabels.learnerEmail"),
                })}
              </MDTypography>

              <MDTypography variant="subtitle2" fontWeight="bold" mb={1}>
                {t("messages.onboarding.sharedAccountWhatYouCanDo")}
              </MDTypography>

              <MDBox component="ul" sx={{ pl: 2.5, mt: 0, mb: 2 }}>
                {[1, 2, 3].map((n) => (
                  <MDBox key={n} component="li" mb={0.5}>
                    <MDTypography variant="body2">
                      {t(`messages.onboarding.sharedAccountFeature${n}`)}
                    </MDTypography>
                  </MDBox>
                ))}
              </MDBox>

              <MDTypography variant="caption" color="secondary" display="block" fontStyle="italic" mb={2}>
                {t("messages.onboarding.sharedAccountNote")}
              </MDTypography>

              {profile.payment_status === "pending" && (
                <InfoBanner icon="warning" color="#e65100" bg="#fff3e0">
                  {t("messages.onboarding.sharedAccountPaymentPending")}
                </InfoBanner>
              )}

              <MDButton variant="contained" color="info" fullWidth onClick={finishHandler}
                sx={{ borderRadius: 2 }} endIcon={<Icon>rocket_launch</Icon>}>
                {t("buttonTitles.finish")}
              </MDButton>
            </MDBox>
          )}

          {/* Screen 6 — Instructions to add child / Coach done */}
          {screen === 6 && (
            <MDBox>
              <ScreenHeader icon="how_to_reg" title={t("instructionToAddChild.title")} />

              {/* Step 1 */}
              <MDTypography variant="subtitle2" fontWeight="bold" mb={0.5}>
                {t("instructionToAddChild.step1.0").split(":")[0]}:
              </MDTypography>
              <MDBox component="ul" sx={{ pl: 2.5, mt: 0, mb: 2 }}>
                <MDBox component="li" mb={0.25}>
                  <MDTypography variant="body2">{t("instructionToAddChild.step1.0").split(":")[1]}</MDTypography>
                </MDBox>
                <MDBox component="li" mb={0.25}>
                  <MDTypography variant="body2">{t("instructionToAddChild.step1.1")}</MDTypography>
                </MDBox>
              </MDBox>

              {/* Step 2 */}
              <MDTypography variant="subtitle2" fontWeight="bold" mb={0.5}>
                {t("instructionToAddChild.step2.0").split(":")[0]}:
              </MDTypography>
              <MDBox component="ul" sx={{ pl: 2.5, mt: 0, mb: 2 }}>
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <MDBox key={i} component="li" mb={0.25}>
                    <MDTypography variant="body2">
                      {i === 0
                        ? t("instructionToAddChild.step2.0").split(":")[1]
                        : t(`instructionToAddChild.step2.${i}`)}
                    </MDTypography>
                  </MDBox>
                ))}
              </MDBox>

              <MDTypography
                variant="caption" color="secondary" display="block" lineHeight={1.6} mt={1} mb={2}
                dangerouslySetInnerHTML={{ __html: t("instructionToAddChild.note") }}
              />

              <InfoBanner icon="check_circle" color="#43a047" bg="#e8f5e9">
                {t("messages.onboarding.completed")}
              </InfoBanner>

              <MDButton variant="contained" color="info" fullWidth onClick={finishHandler}
                sx={{ borderRadius: 2 }}>
                {t("buttonTitles.close")}
              </MDButton>
            </MDBox>
          )}

        </DialogContent>
      </Dialog>

      {/* ── Not Eligible Delete Modal ─────────────────────────────── */}
      <Dialog open={openDeleteModal} onClose={handleCloseDeleteModal} fullWidth maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
        <MDBox
          sx={{
            background: `linear-gradient(135deg, #c62828 0%, #b71c1c 100%)`,
            py: 2.5, px: 3, textAlign: "center",
          }}
        >
          <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#fff" }}>
            {t("messages.onboarding.notEligible").split(".")[0]}
          </MDTypography>
        </MDBox>
        <DialogContent sx={{ px: 4, py: 3, textAlign: "center" }}>
          <MDBox sx={{ width: 64, height: 64, borderRadius: "50%", bgcolor: "#ffebee",
            display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}>
            <Icon sx={{ fontSize: "32px !important", color: "#c62828" }}>block</Icon>
          </MDBox>
          <MDTypography variant="body2" color="text" lineHeight={1.75} mb={3}>
            {t("messages.onboarding.notEligible")}
          </MDTypography>
          <MDButton variant="contained" color="error" fullWidth onClick={handleCloseDeleteModal}
            sx={{ borderRadius: 2 }} startIcon={<Icon>logout</Icon>}>
            <span translate="no" className="notranslate">{inProgress ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : t("buttonTitles.logOut")}</span>
          </MDButton>
        </DialogContent>
      </Dialog>
    </>
  );
};

AdultOnBoardingScreen.defaultProps = {
  role: "gaurdian",
  setOpenLoader: () => {},
};

AdultOnBoardingScreen.propTypes = {
  role: PropTypes.string,
  setOpenLoader: PropTypes.func,
};

export default AdultOnBoardingScreen;
